import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, ImagePlus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useProduct } from "../hooks/useProducts";
import { useCategories } from "@/features/category/hooks/useCategories";
import { ImageCropModal } from "./ImageCropModal";
import { ExtrasManager } from "./ExtrasManager";
import { productsApi } from "@/api/products";
import type { UpdateProductRequest } from "@/types";
import { useProductFormStore } from "@/stores/productFormStore";

const productSchema = z.object({
  name: z
    .string()
    .min(1, "Product name is required")
    .max(100, "Name is too long"),
  description: z.string().max(500, "Description is too long"),
  price: z.number().min(0, "Price must be positive"),
  discount: z.number().min(0).max(100, "Discount must be between 0 and 100"),
  currency: z.string().min(1, "Currency is required"),
  categoryId: z.number().min(1, "Please select a category"),
  visible: z.boolean(),
});

type ProductFormData = z.infer<typeof productSchema>;

export function ProductFormPage() {
  const { id } = useParams<{ id: string }>();
  const productId = id ? parseInt(id) : undefined;
  const isEditing = !!productId;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: product, isLoading: isLoadingProduct } = useProduct(productId);
  const { data: categories, isLoading: isLoadingCategories } = useCategories();

  const {
    extras,
    setExtras,
    imagePreviewUrl,
    setImageFile,
    setImagePreviewUrl,
    croppedImageBlob,
    setCroppedImageBlob,
    reset: resetFormStore,
  } = useProductFormStore();

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      discount: 0,
      currency: "BRL",
      categoryId: 0,
      visible: true,
    },
  });

  // Reset form when product loads
  useEffect(() => {
    if (product) {
      reset({
        name: product.name,
        description: product.description,
        price: product.price,
        discount: product.discount,
        currency: product.currency,
        categoryId: product.category.id,
        visible: product.visible,
      });
      // Map product extras to ProductExtraSelection format
      setExtras(
        product.extras.map((e) => ({
          restaurantExtraId: e.restaurantExtraId,
          priceOverride: e.priceOverride,
        }))
      );
      if (product.image) {
        setImagePreviewUrl(product.image);
      }
    }
  }, [product, reset, setExtras, setImagePreviewUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetFormStore();
    };
  }, [resetFormStore]);

  const createMutation = useMutation({
    mutationFn: productsApi.createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      navigate("/dash/products");
    },
    onError: () => {
      setError("Failed to create product. Please try again.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateProductRequest }) =>
      productsApi.updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      navigate("/dash/products");
    },
    onError: () => {
      setError("Failed to update product. Please try again.");
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImageFile(file);
      setCropModalOpen(true);
    }
    e.target.value = ""; // Reset input
  };

  const handleCropComplete = (croppedBlob: Blob) => {
    const file = new File([croppedBlob], "product-image.jpg", {
      type: "image/jpeg",
    });
    setImageFile(file);
    setCroppedImageBlob(croppedBlob);
    setImagePreviewUrl(URL.createObjectURL(croppedBlob));
    setCropModalOpen(false);
    setSelectedImageFile(null);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreviewUrl(null);
    setCroppedImageBlob(null);
  };

  const onSubmit = async (data: ProductFormData) => {
    setError(null);

    const productData = {
      name: data.name,
      description: data.description,
      price: data.price,
      discount: data.discount,
      currency: data.currency,
      categoryId: data.categoryId,
      visible: data.visible,
      extras: extras,
      image: croppedImageBlob
        ? new File([croppedImageBlob], "product-image.jpg", {
            type: "image/jpeg",
          })
        : undefined,
    };

    if (isEditing && productId) {
      updateMutation.mutate({ id: productId, data: productData });
    } else {
      createMutation.mutate(productData);
    }
  };

  if (isLoadingProduct || isLoadingCategories) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/dash/products")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">
          {isEditing ? "Edit Product" : "New Product"}
        </h1>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Product Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Image */}
            <div className="space-y-2">
              <Label>Image</Label>
              <div className="flex items-start gap-4">
                {imagePreviewUrl ? (
                  <div className="relative">
                    <img
                      src={imagePreviewUrl}
                      alt="Product preview"
                      loading="lazy"
                      decoding="async"
                      className="h-32 w-48 rounded-lg object-cover"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -right-2 -top-2 h-6 w-6"
                      onClick={handleRemoveImage}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label
                    data-testid="image-upload"
                    className="flex h-32 w-48 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50"
                  >
                    <ImagePlus className="h-8 w-8 text-muted-foreground" />
                    <span className="mt-2 text-sm text-muted-foreground">
                      Upload image
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageSelect}
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="e.g., Margherita Pizza"
                {...register("name")}
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <p className="text-sm text-destructive">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                placeholder="Describe your product..."
                className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                {...register("description")}
              />
              {errors.description && (
                <p className="text-sm text-destructive">
                  {errors.description.message}
                </p>
              )}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>Category</Label>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ? field.value.toString() : ""}
                    onValueChange={(value) => field.onChange(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories?.map((category) => (
                        <SelectItem
                          key={category.id}
                          value={category.id.toString()}
                        >
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.categoryId && (
                <p className="text-sm text-destructive">
                  {errors.categoryId.message}
                </p>
              )}
            </div>

            {/* Price and Currency */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="price">Price</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...register("price", { valueAsNumber: true })}
                  aria-invalid={!!errors.price}
                />
                {errors.price && (
                  <p className="text-sm text-destructive">
                    {errors.price.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Currency</Label>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BRL">BRL (R$)</SelectItem>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            {/* Discount */}
            <div className="space-y-2">
              <Label htmlFor="discount">Discount (%)</Label>
              <Input
                id="discount"
                type="number"
                min="0"
                max="100"
                placeholder="0"
                {...register("discount", { valueAsNumber: true })}
                aria-invalid={!!errors.discount}
              />
              {errors.discount && (
                <p className="text-sm text-destructive">
                  {errors.discount.message}
                </p>
              )}
            </div>

            {/* Visibility */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Visible</Label>
                <p className="text-sm text-muted-foreground">
                  Show this product on your menu
                </p>
              </div>
              <Controller
                name="visible"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Extras */}
        <ExtrasManager />

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/dash/products")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Save Changes" : "Create Product"}
          </Button>
        </div>
      </form>

      {/* Image Crop Modal */}
      {selectedImageFile && (
        <ImageCropModal
          open={cropModalOpen}
          onClose={() => {
            setCropModalOpen(false);
            setSelectedImageFile(null);
          }}
          imageFile={selectedImageFile}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
}
